import {
	clearSubmissionCountsCache,
	submissionByID,
	submissionCreate,
	submissionDestroy,
	submissionList,
	submissionUpdate,
} from '@/services/agency/submissions.service'

const emptySubmissionList = () => ({
	data: [],
	meta: {
		pagination: {
			total: 0,
		},
	},
})

const submissionListCache = new Map()
const submissionListPending = new Map()
let submissionListRequestKey = 0

const cacheKey = params => JSON.stringify(params || {})
const clearSubmissionCache = () => {
	submissionListCache.clear()
	submissionListPending.clear()
	clearSubmissionCountsCache()
}

export default {
	namespaced: true,
	state: {
		// SERVICE SUBMISSION
		submissionList: emptySubmissionList(),
		submission: {},

		// STATUS SERVICE SUBMISSION
		submissionStatus: 'pending',
		submissionSaveStatus: 'init',
		submissionDeleteStatus: 'init',
	},
	getters: {
		// SERVICE SUBMISSION
		getSubmissionList(state) {
			return state.submissionList
		},
		getSubmission(state) {
			return state.submission
		},

		// SERVICE STATUS
		getStatusList(state) {
			return state.statusList
		},
		getStatus(state) {
			return state.status
		},

		// SERVICE SUBMISSION
		getSubmissionDeleteStatus(state) {
			return state.submissionDeleteStatus
		},
		getSubmissionSaveStatus(state) {
			return state.submissionSaveStatus
		},
		getSubmissionStatus(state) {
			return state.submissionStatus
		},
	},
	mutations: {
		// SERVICE SUBMISSION
		SUBMISSION_LIST(state, value) {
			state.submissionList = {
				...emptySubmissionList(),
				...value,
				data: Array.isArray(value?.data) ? value.data : [],
				meta: {
					...emptySubmissionList().meta,
					...(value?.meta || {}),
					pagination: {
						...emptySubmissionList().meta.pagination,
						...(value?.meta?.pagination || {}),
					},
				},
			}
		},
		SET_SUBMISSION(state, value) {
			state.submission = value
		},

		// STATUS SERVICE SUBMISSION
		SUBMISSION_STATUS(state, value) {
			state.submissionStatus = value
		},
		SUBMISSION_SAVE_STATUS(state, value) {
			state.submissionSaveStatus = value
		},
		SUBMISSION_DELETE_STATUS(state, value) {
			state.submissionDeleteStatus = value
		},
	},
	actions: {
		// Service Submission
		async fetchSubmissionList({ commit }, params) {
			window.scrollTo({ top: 0, behavior: 'smooth' })
			const key = cacheKey(params)
			const requestKey = ++submissionListRequestKey
			const cached = submissionListCache.get(key)

			if (cached) {
				commit('SUBMISSION_LIST', cached)
				commit('SUBMISSION_STATUS', 'init')
				return cached
			}

			commit('SUBMISSION_STATUS', 'pending')
			try {
				let pendingRequest = submissionListPending.get(key)
				if (!pendingRequest) {
					pendingRequest = submissionList(params)
					submissionListPending.set(key, pendingRequest)
				}

				const { data } = await pendingRequest
				submissionListCache.set(key, data)
				submissionListPending.delete(key)
				if (requestKey !== submissionListRequestKey) return data

				commit('SUBMISSION_STATUS', 'init')
				commit('SUBMISSION_LIST', data)
				return data
			} catch (error) {
				if (requestKey === submissionListRequestKey) {
					commit('SUBMISSION_STATUS', 'init')
				}
				submissionListPending.delete(key)
				throw error
			}
		},
		async fetchSubmissionByID({ commit }, params) {
			commit('SUBMISSION_STATUS', 'pending')
			try {
				const { data } = await submissionByID(params)
				commit('SUBMISSION_STATUS', 'init')
				commit('SET_SUBMISSION', data.data)
				return data
			} catch (error) {
				commit('SUBMISSION_STATUS', 'init')

				return error
			}
		},
		async saveNewSubmission({ commit }, params) {
			commit('SUBMISSION_SAVE_STATUS', 'pending')
			try {
				const { data } = await submissionCreate(params)
				clearSubmissionCache()
				commit('SUBMISSION_SAVE_STATUS', 'init')
				return data
			} catch (error) {
				commit('SUBMISSION_SAVE_STATUS', 'init')
				throw error
			}
		},
		async updateSubmission({ commit }, params) {
			commit('SUBMISSION_SAVE_STATUS', 'pending')
			try {
				const { data } = await submissionUpdate(params)
				clearSubmissionCache()
				commit('SUBMISSION_SAVE_STATUS', 'init')
				return data
			} catch (error) {
				commit('SUBMISSION_SAVE_STATUS', 'init')
				throw error
			}
		},
		async deleteSubmissionByID({ commit }, params) {
			commit('SUBMISSION_DELETE_STATUS', 'pending')
			try {
				const { data } = await submissionDestroy(params)
				clearSubmissionCache()
				commit('SUBMISSION_DELETE_STATUS', 'init')
				return data
			} catch (error) {
				commit('SUBMISSION_DELETE_STATUS', 'error')

				return error
			}
		},
	},
}
